import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { getCoreRowModel } from '@tanstack/react-table';

// MUI Theme
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { Button, MenuItem, Paper, Select, Stack } from '@mui/material';

// Hooks
import { useAppDispatch } from '../../../redux/hook';
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import { useNotifyMessage } from '../../../hooks/useNotifyMessage';

// Redux
import { updateTargetLocationState } from '../../../redux/slices/mapSlice';

// Utils
import { Storage } from '../../../utils/Storage';
import { makeLabelListColumn } from '../../../utils/columns/makeColumn';

// Services
import { getLabels, addOrUpdateLabel, deleteLabel, getLabelChangeLog, exportLabels } from '../../../services/label.service';

// Types
import { Label } from '../../../types/Label';
import { ResourceType } from '../../../types/ResourceType';
import { AppPermissions } from '../../../constants/Permissions';
import { FilterableTypes } from '../../../types/FilterableTypes';
import { CONFIRM_DIALOG_CANCEL, DefaultFilterSelection, SearchBarFilterTypes } from '../../../constants/Constants';

// Common Components
import { useStandardTable as useTable, Table } from '../../common/table/index';
import FormInputCollection from '../../common/FormInputCollection/FormInputCollection';
import ChangeLogList from '../../common/ChangeLog';
import { AuthGuard } from '../../common/AuthGuard';
import SearchBar from '../../common/SearchBar/SearchBar';
import CustomModal from '../../common/withModal';
import withForm from '../../common/withForm';

// Components
import { PaginatedRequest } from '../../../requestModels/paginatedRequest';
import labelForm, { LabelInput } from './labelForm';
import { tablePrimaryButtonSx } from '../../../styles/tableButtonStyles';

const LabelForm = withForm(labelForm);

interface LabelListProps {
  accessKey?: string;
}

export default function LabelListType(props: LabelListProps): JSX.Element {
  const dispatch = useAppDispatch();
  const { notifyMessage } = useNotifyMessage();

  const [changeLogTargetId, setChangeLogTargetId] = useState<string | undefined>(undefined);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const forceReload = () => setParamFilter({ ...paramFilter, changedAt: new Date() });
  const [resourceType, setResourceType] = useState<number | undefined>();
  const [paramFilter, setParamFilter] = useState<any>({
    sortBy: [{ name: '1' }],
  });
  const [isResetting, setIsResetting] = useState(false);

  const { renderDialog, confirm } = useConfirmDialog(CONFIRM_DIALOG_CANCEL);

  useEffect(() => {
    if (isResetting) {
      Storage.setSearchBarFiltering(SearchBarFilterTypes.LABEL, { isMoreFilter: false, filters: [] });
      setParamFilter({ filters: [], sortBy: [{ name: '1' }] });
      setIsResetting(false);
    }
  }, [isResetting, paramFilter.sortBy]);

  const getChangeLog = async (request: PaginatedRequest): Promise<any> => {
    if (changeLogTargetId) return await getLabelChangeLog(changeLogTargetId, request);

    return null;
  };

  const formik = useFormik<LabelInput>({
    initialValues: {
      id: '',
      name: '',
      colorRgb: '',
      resourceType: undefined,
    },
    validationSchema: () => {
      return Yup.object<LabelInput>({
        name: Yup.string().required('This field is required+'),
        colorRgb: Yup.string().required('This field is required'),
        resourceType: Yup.number().required('This field is required'),
      });
    },
    enableReinitialize: true, // resets form only if initialValues is changed,
    validateOnBlur: true,
    onSubmit: async (values) => {
      const { id, name, colorRgb, resourceType } = values;
      const payload = {
        id,
        name,
        colorRgb,
        resourceType,
      } as Label;

      await addOrUpdateLabel(payload)
        .then((res: any) => {
          if (res.status === 200) {
            notifyMessage('Update Label success!', 'success');
            setIsEditDialogOpen(false);
            forceReload();
          } else {
            notifyMessage(res.data);
          }
        })
        .catch((error) => {
          notifyMessage('Unable to update label', 'error');
          console.error('error', error);
        });
    },
  });

  const handleEdit = useCallback((label: Label) => {
    formik.setValues({
      id: label.id,
      name: label.name,
      colorRgb: label.colorRgb || '',
      resourceType: label.resourceType,
    });
    setIsEditDialogOpen(true);
  }, []);

  const handleDelete = useCallback(
    async (id: string) => {
      const { accepted } = await confirm({});
      if (!accepted) return;
      await deleteLabel(id)
        .then(() => {
          notifyMessage('Label has been removed', 'success');
          forceReload();
        })
        .catch(() => {
          notifyMessage('Unable to delete label', 'error');
        });
    },
    [confirm],
  );

  const handleOpenChangeLog = (id: string): void => {
    dispatch(updateTargetLocationState(null));
    setChangeLogTargetId(id);
  };

  const columns = useMemo(() => makeLabelListColumn(handleDelete, handleEdit, handleOpenChangeLog), []);

  const table = useTable<any>({
    columns,
    accessKey: props.accessKey,
    fetchAsync: getLabels,
    exportAsync: exportLabels,
    params: paramFilter,
    serverSide: true,
  });

  const onSearchChange = (filters: any) => {
    let finalFilters = [...filters];
    finalFilters = finalFilters.map((v: any) => {
      v.filterType = FilterableTypes.Contains;
      return v;
    });

    if (resourceType !== undefined) {
      finalFilters.push({
        resourceType: resourceType,
        filterType: FilterableTypes.Equals,
      });
    }
    setParamFilter({ filters: finalFilters, sortBy: paramFilter.sortBy });
    Storage.setSearchBarFiltering(SearchBarFilterTypes.LABEL, { isMoreFilter: true, filters: finalFilters });
  };

  const handleReset = () => {
    setResourceType(undefined);
    setIsResetting(true);
  };

  return (
    <React.Fragment>
      <AuthGuard permission={AppPermissions.Label.Edit}>
        {renderDialog()}
        <Stack direction='row' spacing={1} justifyContent='flex-end'>
          <Button
            onClick={() => {
              formik.resetForm();
              formik.setValues({
                id: '',
                name: '',
                colorRgb: '',
                resourceType: undefined,
              });
              setIsEditDialogOpen(true);
            }}
            variant='contained'
            size='small'
            endIcon={<AddCircleOutlineOutlinedIcon style={{ fontSize: 20 }} />}
            sx={tablePrimaryButtonSx}
          >
            Add Label
          </Button>
        </Stack>

        <CustomModal
          title='Label Type'
          isOpen={isEditDialogOpen}
          isValid={formik.isValid}
          setIsOpen={setIsEditDialogOpen}
          onConfirm={formik.handleSubmit}
          maxWidth='xs'
        >
          <LabelForm formRef={formik} />
        </CustomModal>
      </AuthGuard>
      <CustomModal
        title='ChangeLog'
        isOpen={changeLogTargetId !== undefined}
        setIsOpen={() => setChangeLogTargetId(undefined)} //Need to close Model when the user click OK/Cancel
        maxWidth='md'
      >
        <ChangeLogList fetchAsync={getChangeLog} />
      </CustomModal>

      <SearchBar
        pageKey={SearchBarFilterTypes.LABEL}
        onSearch={onSearchChange}
        onReset={handleReset}
        allowSearchAll={true}
        columns={columns}
        components={
          <div style={{ display: 'flex', marginBottom: 5 }}>
            <FormInputCollection label='Type' style={{ width: '150px' }}>
              <Select
                value={resourceType ?? DefaultFilterSelection.DefaultResourceType}
                variant='outlined'
                id='resourceType'
                name='status'
                size='small'
                style={{ width: '150px' }}
                onChange={(e: any) => {
                  setResourceType(e.target.value === DefaultFilterSelection.DefaultResourceType ? undefined : e.target.value);
                }}
              >
                <MenuItem value={DefaultFilterSelection.DefaultResourceType}>All</MenuItem>
                {Object.keys(ResourceType)
                  .filter((key) => {
                    const resourceTypeId = ResourceType[key];
                    // we need show show only 4 ResourceType (Device=1, Equipment=2, Reader=3, Facility=4)
                    return !isNaN(Number(resourceTypeId)) && resourceTypeId <= 4;
                  })
                  .map((resourceType, index: number) => {
                    return (
                      <MenuItem key={index} value={ResourceType[resourceType]}>
                        {resourceType}
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormInputCollection>
          </div>
        }
      />
      <Paper>
        <Table {...table} showSearchBar={false} showColumnToggler={false} showAction={true} getCoreRowModel={getCoreRowModel()} />
      </Paper>
    </React.Fragment>
  );
}
